/**
 * LongTermResultsPage - Long-term energy benefits analysis results.
 *
 * Props:
 *   - scenarioId: UUID of the scenario
 *   - params: Long-term analysis parameters
 *
 * Displays:
 *   1. Annual KPI Projections (self-sufficiency, self-consumption, grid dependence)
 *   2. Cumulative Savings over time
 *   3. Asset Utilization breakdown
 *   4. Summary KPI Cards
 *
 * Data sourcing:
 *   - Calls /v1/kpis/long-term on mount via useLongTermKPIs().
 *   - On API error (e.g. cba-api not yet implemented) falls back to
 *     czechLongTermResults() mock so the UI remains functional.
 */

import React, { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap'
import Chart from 'react-apexcharts'
import { czechLongTermResults } from '../data/czechEnergyProfiles'
import { useLongTermKPIs } from '../hooks/useKPIs'
import { kpisAPI } from '../api/kpis'
import { resultsAPI } from '../api/results'
import { exportLongTermResults } from '../utils/exportCSV'
import { getCountryDefaults } from '../data/countryDefaults'
import { useAuth } from '../context/AuthContext'
import { useTranslation, Trans } from 'react-i18next'

// EUR -> CZK exchange rate used for display conversion.
const EUR_TO_CZK = 25

/**
 * Map a raw backend long-term KPI response to the shape that the chart/card
 * rendering code expects (same structure as czechLongTermResults()).
 *
 * Backend response (from /v1/kpis/long-term) is expected to include:
 *   year_labels[]: string[]           (e.g. ["Year 0", "Year 1", ...])
 *   annual: {
 *     self_sufficiency_pct[]: number[]
 *     self_consumption_pct[]: number[]
 *     cumulative_savings_eur[]: number[]
 *   }
 *   monthly_utilization: {
 *     months[]: string[]
 *     pv_utilization_pct[]: number[]
 *     battery_cycles[]: number[]
 *     flex_load_hours[]: number[]
 *   }
 *   summary: {
 *     total_energy_cost_savings_eur: number
 *     avg_self_sufficiency_pct: number
 *     total_co2_avoided_tonnes: number
 *     roi_pct: number
 *   }
 */
function mapBackendResponse(raw, isCZK, numYears) {
  const rate = isCZK ? EUR_TO_CZK : 1

  // --- year labels ---
  const years = raw.year_labels ?? raw.years ?? Array.from({ length: numYears }, (_, i) => `Year ${i + 1}`)

  // --- annual KPI arrays ---
  const annual = raw.annual || {}
  const selfSufficiency = annual.self_sufficiency_pct ?? annual.selfSufficiency ?? []
  const selfConsumption = annual.self_consumption_pct ?? annual.selfConsumption ?? []
  const gridDependence = (annual.grid_dependence_pct ?? annual.gridDependence)
    ? (annual.grid_dependence_pct ?? annual.gridDependence)
    : selfSufficiency.map((v) => +(100 - v).toFixed(1))

  const cumulativeSavings = (annual.cumulative_savings_eur ?? annual.cumulativeSavings ?? []).map(
    (v) => Math.round(v * rate)
  )

  // --- savings breakdown (self-consumption vs export) ---
  const cumulativeSCSavings = (annual.cumulativeSelfConsumptionSavings ?? []).map(
    (v) => Math.round(v * rate)
  )
  const cumulativeExportRevenue = (annual.cumulativeExportRevenue ?? []).map(
    (v) => Math.round(v * rate)
  )

  // --- annual energy flows (for Energy Flow chart) ---
  const selfConsumedKwh = annual.self_consumed_kwh ?? []
  const gridImportKwh = annual.grid_import_kwh ?? []
  const gridExportKwh = annual.grid_export_kwh ?? []
  const loadKwh = annual.load_kwh ?? []

  // --- summary KPIs ---
  const s = raw.summary || {}
  const summary = {
    totalSavings: Math.round((s.total_energy_cost_savings_eur ?? s.totalSavings ?? 0) * rate),
    avgSelfSufficiency: +(s.avg_self_sufficiency_pct ?? s.avgSelfSufficiency ?? 0),
    totalCO2Avoided: +(s.total_co2_avoided_tonnes ?? s.totalCO2Avoided ?? 0),
    roi: +(s.roi_pct ?? s.roi ?? 0),
  }

  return {
    years, selfSufficiency, selfConsumption, gridDependence,
    cumulativeSavings, cumulativeSCSavings, cumulativeExportRevenue,
    selfConsumedKwh, gridImportKwh, gridExportKwh, loadKwh,
    summary,
  }
}

/**
 * Generate dynamic insights from the actual KPI results and input parameters.
 * Every insight is computed from data — nothing is static or hardcoded text.
 * When baseline is present, insights focus on the investment delta.
 */
function generateInsights(results, baseline, inputs, numYears, currencySymbol, t) {
  if (!results) return []
  const insights = []
  const ss = results.selfSufficiency || []
  const sc = results.selfConsumption || []
  const gd = results.gridDependence || []
  const savings = results.cumulativeSavings || []

  const ssFirst = ss[0]
  const ssLast = ss[ss.length - 1]
  const scFirst = sc[0]
  const gdFirst = gd[0]
  const gdLast = gd[gd.length - 1]

  // ---- Investment impact (baseline comparison) ----
  if (baseline) {
    const bSS = baseline.selfSufficiency || []
    const bGD = baseline.gridDependence || []
    const bSavings = baseline.cumulativeSavings || []

    // Year 1 improvement
    const ssLift = ssFirst - (bSS[0] || 0)
    const gdDrop = (bGD[0] || 0) - gdFirst
    if (ssLift > 0.5) {
      const dir = t('long_term_results.dir_lifts')
      const gdDir = t(gdDrop > 0 ? 'long_term_results.gddir_reduces' : 'long_term_results.gddir_increases')
      // Also compute the average lift (matches KPI card)
      const avgSS = results.summary?.avgSelfSufficiency ?? ssFirst
      const avgBSS = baseline.summary?.avgSelfSufficiency ?? (bSS[0] || 0)
      const avgLift = avgSS - avgBSS
      insights.push({
        icon: '\u{1F4AA}',
        text: <Trans i18nKey="long_term_results.insight_investment_lift" components={{ b: <strong /> }} values={{ dir, ssLift: ssLift.toFixed(1), bSS0: (bSS[0] || 0).toFixed(1), ssFirst: ssFirst.toFixed(1), gdDir, gdDrop: gdDrop.toFixed(1), avgLift: avgLift.toFixed(1) }} />,
      })
    }

    // Final year gap
    const ssLiftFinal = ssLast - (bSS[bSS.length - 1] || 0)
    if (ssLiftFinal > 0.5 && Math.abs(ssLiftFinal - ssLift) > 0.3) {
      const trend = t(ssLiftFinal > ssLift ? 'long_term_results.trend_grows' : 'long_term_results.trend_narrows')
      insights.push({
        icon: '\u{1F52E}',
        text: <Trans i18nKey="long_term_results.insight_final_year" components={{ b: <strong /> }} values={{ numYears, trend, ssLiftFinal: ssLiftFinal.toFixed(1), ssLast: ssLast.toFixed(1), bSSLast: (bSS[bSS.length - 1] || 0).toFixed(1) }} />,
      })
    }

    // Additional savings from investment
    const totalWithInv = savings[savings.length - 1] || 0
    const totalBaseline = bSavings[bSavings.length - 1] || 0
    const addlSavings = totalWithInv - totalBaseline
    if (addlSavings > 0) {
      insights.push({
        icon: '\u{1F4B0}',
        text: <Trans i18nKey="long_term_results.insight_additional_savings" components={{ b: <strong /> }} values={{ addlSavings: addlSavings.toLocaleString(), currency: currencySymbol, numYears, totalWithInv: totalWithInv.toLocaleString(), totalBaseline: totalBaseline.toLocaleString() }} />,
      })
    }

    // Baseline grid dependence crossover
    const bCrossover = bGD.findIndex(v => v > 50)
    const wiCrossover = gd.findIndex(v => v > 50)
    if (bCrossover >= 0 && (bGD[0] || 0) < 50) {
      if (wiCrossover < 0) {
        insights.push({
          icon: '\u{1F6E1}',
          text: <Trans i18nKey="long_term_results.insight_prevent_crossover" components={{ b: <strong /> }} values={{ year: bCrossover + 1, gdLast: gdLast.toFixed(1), numYears }} />,
        })
      } else if (wiCrossover > bCrossover) {
        insights.push({
          icon: '\u{1F6E1}',
          text: <Trans i18nKey="long_term_results.insight_delay_crossover" components={{ b: <strong /> }} values={{ baseYear: bCrossover + 1, wiYear: wiCrossover + 1, years: wiCrossover - bCrossover }} />,
        })
      }
    }
  }

  // ---- SC% vs absolute kWh explainer (when baseline SC% > investment SC%) ----
  if (baseline) {
    const bSC = baseline.selfConsumption || []
    if ((bSC[0] || 0) > scFirst + 2) {
      insights.push({
        icon: '\u{1F4A1}',
        text: <Trans i18nKey="long_term_results.insight_sc_explainer" components={{ b: <strong /> }} values={{ bSC0: (bSC[0] || 0).toFixed(1), scFirst: scFirst.toFixed(1) }} />,
      })
    }
  }

  // ---- Trajectory explanation (always shown) ----
  const ssDelta = ssLast - ssFirst
  const scLast = sc[sc.length - 1]
  const scDelta = scLast - scFirst
  if (ssDelta < -1 && scDelta > 1) {
    insights.push({
      icon: '\u{1F4C9}',
      text: inputs
        ? <Trans i18nKey="long_term_results.insight_trajectory_decline_rates" components={{ b: <strong /> }} values={{ ssFirst: ssFirst.toFixed(1), ssLast: ssLast.toFixed(1), numYears, scFirst: scFirst.toFixed(1), scLast: scLast.toFixed(1), pvRate: (inputs.pvDegradation * 100).toFixed(1), loadRate: (inputs.loadGrowthRate * 100).toFixed(1) }} />
        : <Trans i18nKey="long_term_results.insight_trajectory_decline" components={{ b: <strong /> }} values={{ ssFirst: ssFirst.toFixed(1), ssLast: ssLast.toFixed(1), numYears, scFirst: scFirst.toFixed(1), scLast: scLast.toFixed(1) }} />,
    })
  } else if (ssDelta > 1) {
    insights.push({
      icon: '\u{1F4C8}',
      text: <Trans i18nKey="long_term_results.insight_trajectory_improve" components={{ b: <strong /> }} values={{ ssFirst: ssFirst.toFixed(1), ssLast: ssLast.toFixed(1) }} />,
    })
  } else {
    insights.push({
      icon: '\u{2796}',
      text: <Trans i18nKey="long_term_results.insight_trajectory_stable" components={{ b: <strong /> }} values={{ ssFirst: ssFirst.toFixed(1) }} />,
    })
  }

  // Grid dependence crossover (only if not already covered by baseline comparison)
  if (!baseline && gdLast > gdFirst + 2) {
    const crossoverYear = gd.findIndex(v => v > 50)
    if (crossoverYear >= 0 && gdFirst < 50) {
      insights.push({
        icon: '\u{26A0}',
        text: <Trans i18nKey="long_term_results.insight_grid_crossover" components={{ b: <strong /> }} values={{ year: crossoverYear + 1 }} />,
      })
    } else {
      insights.push({
        icon: '\u{1F50C}',
        text: <Trans i18nKey="long_term_results.insight_grid_rises" components={{ b: <strong /> }} values={{ gdFirst: gdFirst.toFixed(1), gdLast: gdLast.toFixed(1), perYear: ((gdLast - gdFirst) / numYears).toFixed(1) }} />,
      })
    }
  }

  // ---- Savings & ROI ----
  if (!baseline) {
    const totalSavings = savings[savings.length - 1] || 0
    const roi = results.summary?.roi || 0
    if (totalSavings > 0 && inputs?.totalCapex > 0) {
      const paybackYear = savings.findIndex(s => s >= inputs.totalCapex * (currencySymbol === 'CZK' ? 25 : 1))
      if (paybackYear >= 0) {
        insights.push({
          icon: '\u{1F4B0}',
          text: <Trans i18nKey="long_term_results.insight_payback_reached" components={{ b: <strong /> }} values={{ year: paybackYear + 1, totalSavings: totalSavings.toLocaleString(), currency: currencySymbol, numYears, roi }} />,
        })
      } else {
        insights.push({
          icon: '\u{1F4B0}',
          text: <Trans i18nKey="long_term_results.insight_payback_beyond" components={{ b: <strong /> }} values={{ totalSavings: totalSavings.toLocaleString(), currency: currencySymbol, numYears, roi }} />,
        })
      }
    }
  }

  // CO2 impact
  const co2 = results.summary?.totalCO2Avoided
  if (co2 > 0) {
    const carEquivalent = (co2 / 2.3).toFixed(0)
    const baseCo2 = baseline?.summary?.totalCO2Avoided || 0
    const addlCo2 = co2 - baseCo2
    insights.push({
      icon: '\u{1F33F}',
      text: <>
        <Trans i18nKey="long_term_results.insight_co2" components={{ b: <strong /> }} values={{ co2: co2.toFixed(1), numYears, cars: carEquivalent }} />
        {baseline && addlCo2 > 0.5 && <Trans i18nKey="long_term_results.insight_co2_extra" components={{ b: <strong /> }} values={{ addlCo2: addlCo2.toFixed(1) }} />}
      </>,
    })
  }

  // Asset composition context
  if (inputs) {
    const parts = []
    if (inputs.realPvKwp > 0) parts.push(t('long_term_results.portfolio_existing_pv', { n: inputs.realPvKwp }))
    if (inputs.hasWhatIfPv) parts.push(t('long_term_results.portfolio_new_pv', { n: inputs.whatIfPvKwp }))
    if (inputs.realBattKwh > 0) parts.push(t('long_term_results.portfolio_existing_battery', { n: inputs.realBattKwh }))
    if (inputs.hasWhatIfBatt) parts.push(t('long_term_results.portfolio_new_battery', { n: inputs.whatIfBattKwh }))
    if (inputs.realWindKwp > 0) parts.push(t('long_term_results.portfolio_existing_wind', { n: inputs.realWindKwp }))
    if (inputs.hasWhatIfWind) parts.push(t('long_term_results.portfolio_new_wind', { n: inputs.whatIfWindKw }))

    if (parts.length > 0) {
      const totalPvGen = Math.round(inputs.pvKwp * inputs.pvYield)
      const genLoadRatio = inputs.annualLoad > 0
        ? ((totalPvGen + (inputs.windKwp * 0.22 * 8760)) / inputs.annualLoad * 100).toFixed(0)
        : '?'
      const conclusion = parseInt(genLoadRatio) > 100
        ? t('long_term_results.conclusion_net_exporter')
        : parseInt(genLoadRatio) > 70
          ? t('long_term_results.conclusion_covers_most')
          : t('long_term_results.conclusion_expand')

      insights.push({
        icon: '\u{2699}',
        text: <Trans i18nKey="long_term_results.insight_asset_portfolio" components={{ b: <strong /> }} values={{ parts: parts.join(', '), ratio: genLoadRatio, load: Math.round(inputs.annualLoad).toLocaleString(), conclusion }} />,
      })
    }
  }

  // Annual savings growth rate (driven by tariff escalation)
  if (savings.length >= 2 && inputs?.priceEscalation > 0) {
    const annualSavings = savings.map((s, i) => i === 0 ? s : s - savings[i - 1])
    const firstAnnual = annualSavings[0]
    const lastAnnual = annualSavings[annualSavings.length - 1]
    if (firstAnnual > 0 && lastAnnual > firstAnnual) {
      insights.push({
        icon: '\u{1F4C8}',
        text: <Trans i18nKey="long_term_results.insight_savings_growth" components={{ b: <strong /> }} values={{ first: firstAnnual.toLocaleString(), last: lastAnnual.toLocaleString(), currency: currencySymbol, escalation: (inputs.priceEscalation * 100).toFixed(0) }} />,
      })
    }
  }

  return insights
}

function LongTermResultsPage({ scenarioId, params, communityId: propCommunityId, community: propCommunity, currency: propCurrency }) {
  const { canEdit } = useAuth()
  const { t } = useTranslation()
  const timeHorizon = params?.timeHorizon || '5_years'
  const numYears = timeHorizon === '1_year' ? 1
    : timeHorizon === '5_years' ? 5
    : timeHorizon === '20_years' ? 20
    : 10
  const currency = propCurrency || params?.currency || 'EUR'
  const sym = currency === 'CZK' ? 'CZK' : '€'
  const isCZK = currency === 'CZK'
  const communityId = propCommunityId || null
  const fmtK = (v) => v != null ? `${(v / 1000).toFixed(0)}k ${sym}` : ''
  const fmtV = (v) => v != null ? `${v.toLocaleString()} ${sym}` : ''

  const kpiMutation = useLongTermKPIs()
  const [results, setResults] = useState(null)
  const [baselineResults, setBaselineResults] = useState(null) // existing-only projection for comparison
  const [inputSnapshot, setInputSnapshot] = useState(null) // captured inputs for explainability
  const [isLoading, setIsLoading] = useState(true)
  // 'loading' | 'api' | 'mock'
  const [dataSource, setDataSource] = useState('loading')
  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState(null)  // null | 'success' | 'error'

  const handleSaveResults = async () => {
    if (!results) return
    const saveId = communityId || scenarioId
    if (!saveId) return
    setIsSaving(true)
    setSaveStatus(null)
    try {
      await resultsAPI.save(saveId, {
        result_type: 'kpis',
        name: `Long-term KPIs (${timeHorizon}) - ${new Date().toLocaleDateString()}`,
        parameters: { ...params, scenario_id: scenarioId },
        result_data: { withInvestment: results, ...(baselineResults ? { existingOnly: baselineResults } : {}) },
      })
      setSaveStatus('success')
      setTimeout(() => setSaveStatus(null), 3000)
    } catch (error) {
      console.error('Failed to save long-term KPI results:', error)
      setSaveStatus('error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleExportCSV = () => {
    if (!results) return
    exportLongTermResults(results, currency, baselineResults)
  }

  useEffect(() => {
    const runKPIs = async () => {
      setIsLoading(true)
      try {
        // Country-aware defaults
        const country = params?.country || 'Czech Republic'
        const cd = getCountryDefaults(country)

        // ---- Extract REAL asset capacities from community ----
        const buildings = propCommunity?.buildings || []
        const communityAssets = propCommunity?.community_assets || []

        // Real PV: community PV farms + building rooftop PV
        const realPvKwp = communityAssets
          .filter(a => ['solar_pv_farm', 'ground_mounted_pv', 'pv'].includes(a.asset_type))
          .reduce((sum, a) => sum + (a.specs?.capacity_kwp || 0), 0)
          + buildings.reduce((sum, b) =>
            sum + (b.building_assets || [])
              .filter(a => a.asset_type === 'rooftop_pv')
              .reduce((s, a) => s + (a.specs?.capacity_kwp || 0), 0)
          , 0)

        // Real battery: community + building batteries
        const realBattKwh = communityAssets
          .filter(a => ['community_battery', 'battery'].includes(a.asset_type))
          .reduce((sum, a) => sum + (a.specs?.capacity_kwh || 0), 0)
          + buildings.reduce((sum, b) =>
            sum + (b.building_assets || [])
              .filter(a => a.asset_type === 'battery')
              .reduce((s, a) => s + (a.specs?.capacity_kwh || 0), 0)
          , 0)

        // Real wind: community wind assets
        const realWindKwp = communityAssets
          .filter(a => ['wind_turbine', 'wind'].includes(a.asset_type))
          .reduce((sum, a) => sum + (a.specs?.capacity_kwp || a.specs?.rated_power_kw || 0), 0)

        // Base capacities: use real community data, fall back to form params, then defaults
        let basePvKwp = realPvKwp > 0 ? realPvKwp : (params?.pvSizeKwp || 0)
        let baseBattKwh = realBattKwh > 0 ? realBattKwh : (params?.battCapacityKwh || 0)
        let baseWindKwp = realWindKwp > 0 ? realWindKwp : (params?.windCapacityKwp || 0)

        // Apply what-if additions ON TOP of real values
        if (params?.addPvSystem && params?.newPvCapacityKwp > 0) {
          basePvKwp += params.newPvCapacityKwp
        }
        if (params?.addBattery && params?.newBatteryCapacityKwh > 0) {
          baseBattKwh += params.newBatteryCapacityKwh
        }
        if (params?.addWindTurbine && params?.newWindCapacityKw > 0) {
          baseWindKwp += params.newWindCapacityKw
        }

        const pvKwp = basePvKwp
        const battKwh = baseBattKwh
        const windKwp = baseWindKwp

        // Annual load: prefer computed from datasets (passed via params), then form, then default
        const annualLoad = params?.annualLoadKwh || 65000

        // Load growth: from form slider (percentage) or direct rate
        const loadGrowthRate = params?.load_growth_rate
          || (params?.loadGrowthPct != null ? params.loadGrowthPct / 100 : 0.01)
        // Tariff escalation: from form slider or direct rate
        const priceEscalation = params?.electricity_price_escalation
          || params?.priceEscalation
          || (params?.tariffChangePct != null ? params.tariffChangePct / 100 : 0.03)

        // Currency conversion: backend always works in EUR.
        const paramsCurrency = params?.currency || currency
        const toEur = paramsCurrency === 'CZK' ? (1 / EUR_TO_CZK) : 1

        const pvUnitCost = params?.pvUnitCostEur
          || (params?.pvCapexPerKwp ? params.pvCapexPerKwp * toEur : null)
          || 833
        const battUnitCost = params?.battUnitCostEur
          || (params?.battCapexPerKwh ? params.battCapexPerKwh * toEur : null)
          || 750
        const windUnitCost = params?.windUnitCostEur || 1200
        const pvCapex = pvKwp * pvUnitCost
        const battCapex = battKwh * battUnitCost
        const windCapex = windKwp * windUnitCost
        const totalCapex = pvCapex + battCapex + windCapex

        // Tariffs: use params if provided, then community metadata, then error (no hardcoded fallback)
        const communityMeta = propCommunity?.metadata || {}
        const tariffImportEur = params?.tariffImportEur
          || (params?.electricityPurchasePricePerKwh ? params.electricityPurchasePricePerKwh * toEur : null)
          || (communityMeta.import_tariff ? communityMeta.import_tariff * toEur : null)
          || (() => { console.error('Community import tariff not configured for long-term KPIs'); return 0 })()
        const tariffExportEur = params?.tariffExportEur
          || (params?.feedInTariffPerKwh ? params.feedInTariffPerKwh * toEur : null)
          || (communityMeta.export_tariff ? communityMeta.export_tariff * toEur : null)
          || (() => { console.error('Community export tariff not configured for long-term KPIs'); return 0 })()

        // PV yield: use community metadata if available (real measured yield)
        const pvYield = communityMeta.pv_yield_kwh_per_kwp || cd.pvYieldKwhPerKwp

        const pvDegradation = params?.pvAnnualDegradationPct != null
          ? params.pvAnnualDegradationPct / 100 : 0.005

        // Capture inputs for the explainability panel
        setInputSnapshot({
          pvKwp, windKwp, battKwh, annualLoad, pvYield,
          loadGrowthRate, priceEscalation, pvDegradation,
          tariffImportEur, tariffExportEur, totalCapex, country,
          // Track what came from real data vs defaults
          realPvKwp, realBattKwh, realWindKwp,
          hasWhatIfPv: !!(params?.addPvSystem && params?.newPvCapacityKwp > 0),
          whatIfPvKwp: params?.newPvCapacityKwp || 0,
          hasWhatIfBatt: !!(params?.addBattery && params?.newBatteryCapacityKwh > 0),
          whatIfBattKwh: params?.newBatteryCapacityKwh || 0,
          hasWhatIfWind: !!(params?.addWindTurbine && params?.newWindCapacityKw > 0),
          whatIfWindKw: params?.newWindCapacityKw || 0,
        })

        // Shared request params (same for both scenarios)
        const sharedParams = {
          community_id: communityId || scenarioId,
          analysis_period_years: numYears,
          annual_load_kwh: annualLoad,
          pv_yield_kwh_per_kwp: pvYield,
          load_growth_rate: loadGrowthRate,
          co2_factor_kg_kwh: params?.co2Factor || cd.co2FactorKgPerKwh,
          tariff_import_eur_kwh: tariffImportEur,
          tariff_export_eur_kwh: tariffExportEur,
          electricity_price_escalation: priceEscalation,
          pv_degradation_rate: pvDegradation,
          country,
        }

        // What-if scenario: existing + new assets
        const whatIfRequest = {
          ...sharedParams,
          pv_capacity_kwp: pvKwp,
          wind_capacity_kwp: windKwp,
          battery_capacity_kwh: battKwh,
          capex: totalCapex,
        }

        // Detect if what-if assets were added
        const hasWhatIfAssets = (params?.addPvSystem && params?.newPvCapacityKwp > 0)
          || (params?.addBattery && params?.newBatteryCapacityKwh > 0)
          || (params?.addWindTurbine && params?.newWindCapacityKw > 0)

        // Fire both requests in parallel: what-if + baseline (existing-only)
        const baselinePvKwp = realPvKwp > 0 ? realPvKwp : (params?.pvSizeKwp || 0)
        const baselineBattKwh = realBattKwh > 0 ? realBattKwh : (params?.battCapacityKwh || 0)
        const baselineWindKwp = realWindKwp > 0 ? realWindKwp : (params?.windCapacityKwp || 0)
        const baselineCapex = baselinePvKwp * pvUnitCost + baselineBattKwh * battUnitCost + baselineWindKwp * windUnitCost

        const baselineRequest = hasWhatIfAssets ? {
          ...sharedParams,
          pv_capacity_kwp: baselinePvKwp,
          wind_capacity_kwp: baselineWindKwp,
          battery_capacity_kwh: baselineBattKwh,
          capex: baselineCapex,
        } : null

        const [response, baselineResponse] = await Promise.all([
          kpiMutation.mutateAsync(whatIfRequest),
          baselineRequest ? kpisAPI.longTerm(baselineRequest) : Promise.resolve(null),
        ])

        // The axios interceptor already unwraps .data, so `response` IS the JSON body.
        setDataSource('api')
        setResults(mapBackendResponse(response, isCZK, numYears))
        if (baselineResponse) {
          setBaselineResults(mapBackendResponse(baselineResponse, isCZK, numYears))
        } else {
          setBaselineResults(null)
        }
      } catch (error) {
        console.warn('Long-term KPI API call failed — falling back to mock data:', error.message)
        // Graceful fallback: keep the page functional even when cba-api is down.
        setDataSource('mock')
        setResults(czechLongTermResults({ numYears, currency }))
      } finally {
        setIsLoading(false)
      }
    }

    runKPIs()
    // We intentionally omit kpiMutation from the dependency array — it is
    // a stable reference from useMutation and re-including it causes loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId, numYears, currency, propCommunity])

  // --- Loading / empty states ---
  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" style={{ color: 'var(--color-primary)' }} />
        <p className="mt-3" style={{ color: 'var(--color-neutral-600)' }}>
          {t('long_term_results.loading')}
        </p>
      </div>
    )
  }

  if (!results) {
    return (
      <div className="text-center p-5" style={{ color: 'var(--color-neutral-600)' }}>
        {t('results.no_results_available')}
      </div>
    )
  }

  // --- Build chart series: with-investment + optional baseline overlay ---
  const hasBaseline = !!baselineResults

  // ---- KPI Projections Chart: auto-scale Y-axis to data range ----
  const allKpiValues = [
    ...results.selfSufficiency, ...results.selfConsumption, ...results.gridDependence,
    ...(hasBaseline ? [...baselineResults.selfSufficiency, ...baselineResults.selfConsumption, ...baselineResults.gridDependence] : []),
  ].filter(v => v != null)
  const kpiMin = Math.max(0, Math.floor((Math.min(...allKpiValues) - 5) / 10) * 10)
  const kpiMax = Math.min(100, Math.ceil((Math.max(...allKpiValues) + 5) / 10) * 10)

  const kpiColors = hasBaseline
    ? ['#27AE60', '#3498DB', '#E74C3C', '#27AE60', '#3498DB', '#E74C3C']
    : ['#27AE60', '#3498DB', '#E74C3C']
  const kpiStrokeWidth = hasBaseline ? [3, 3, 3, 2, 2, 2] : [3, 3, 3]
  const kpiStrokeDash = hasBaseline ? [0, 0, 0, 6, 6, 6] : [0, 0, 0]

  const kpiProjectionsOptions = {
    chart: {
      type: 'line',
      height: 350,
      toolbar: { show: true },
      animations: { enabled: true, speed: 800 },
    },
    colors: kpiColors,
    stroke: {
      width: kpiStrokeWidth,
      curve: 'smooth',
      dashArray: kpiStrokeDash,
    },
    dataLabels: { enabled: false },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
    },
    xaxis: {
      categories: results.years,
      title: { text: t('results.time_period') },
    },
    yaxis: {
      title: { text: t('results.percentage') },
      min: kpiMin,
      max: kpiMax,
      labels: {
        formatter: (value) => value != null ? `${value.toFixed(0)}%` : '',
      },
    },
    markers: {
      size: hasBaseline ? [5, 5, 5, 0, 0, 0] : [5, 5, 5],
      hover: { size: 7 },
    },
    tooltip: {
      y: {
        formatter: (value) => value != null ? `${value.toFixed(1)}%` : '',
      },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
    },
  }

  const kpiProjectionsSeries = [
    { name: hasBaseline ? t('long_term_results.series_ss_inv') : t('results.self_sufficiency'), data: results.selfSufficiency },
    { name: hasBaseline ? t('long_term_results.series_sc_inv') : t('results.self_consumption'), data: results.selfConsumption },
    { name: hasBaseline ? t('long_term_results.series_gd_inv') : t('results.grid_dependence'), data: results.gridDependence },
    ...(hasBaseline ? [
      { name: t('long_term_results.series_ss_existing'), data: baselineResults.selfSufficiency },
      { name: t('long_term_results.series_sc_existing'), data: baselineResults.selfConsumption },
      { name: t('long_term_results.series_gd_existing'), data: baselineResults.gridDependence },
    ] : []),
  ]

  // ---- Cumulative Savings Chart: stacked area + baseline dotted lines ----
  const hasSavingsBreakdown = results.cumulativeSCSavings?.length > 0
  const hasBaselineSavings = hasBaseline && baselineResults.cumulativeSCSavings?.length > 0

  // Build colors, stroke widths, and dash arrays dynamically
  const savingsSeriesConfig = []
  // With-investment stacked areas
  if (hasSavingsBreakdown) {
    savingsSeriesConfig.push({ color: '#8E44AD', width: 2, dash: 0 }) // Avoided Import (inv)
    savingsSeriesConfig.push({ color: '#3498DB', width: 2, dash: 0 }) // Export Revenue (inv)
  } else {
    savingsSeriesConfig.push({ color: '#9B59B6', width: 2, dash: 0 }) // Total (inv)
  }
  // Baseline dotted lines
  if (hasBaselineSavings) {
    savingsSeriesConfig.push({ color: '#C39BD3', width: 2, dash: 6 }) // Avoided Import (existing)
    savingsSeriesConfig.push({ color: '#85C1E9', width: 2, dash: 6 }) // Export Revenue (existing)
  } else if (hasBaseline) {
    savingsSeriesConfig.push({ color: '#BDC3C7', width: 2, dash: 6 }) // Total (existing)
  }

  const cumulativeSavingsOptions = {
    chart: {
      type: 'area',
      height: 350,
      stacked: false, // disable global stacking — we control layering via series order
      toolbar: { show: false },
      animations: { enabled: true, speed: 800 },
    },
    colors: savingsSeriesConfig.map(s => s.color),
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.6,
        opacityTo: 0.1,
        stops: [0, 90, 100],
      },
    },
    stroke: {
      width: savingsSeriesConfig.map(s => s.width),
      curve: 'smooth',
      dashArray: savingsSeriesConfig.map(s => s.dash),
    },
    dataLabels: { enabled: false },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '11px',
    },
    xaxis: {
      categories: results.years,
      title: { text: t('results.time_period') },
    },
    yaxis: {
      title: { text: t('long_term_results.savings_axis', { currency }) },
      labels: {
        formatter: (value) => value != null ? fmtK(value) : '',
      },
    },
    tooltip: {
      y: {
        formatter: (value) => value != null ? fmtV(value) : '',
      },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
    },
  }

  const cumulativeSavingsSeries = []
  if (hasSavingsBreakdown) {
    cumulativeSavingsSeries.push(
      { name: t('long_term_results.series_avoided_import_inv'), data: results.cumulativeSCSavings },
      { name: t('long_term_results.series_export_revenue_inv'), data: results.cumulativeExportRevenue },
    )
  } else {
    cumulativeSavingsSeries.push(
      { name: hasBaseline ? t('long_term_results.series_total_inv') : t('long_term_results.series_cumulative_savings'), data: results.cumulativeSavings },
    )
  }
  if (hasBaselineSavings) {
    cumulativeSavingsSeries.push(
      { name: t('long_term_results.series_avoided_import_existing'), data: baselineResults.cumulativeSCSavings },
      { name: t('long_term_results.series_export_revenue_existing'), data: baselineResults.cumulativeExportRevenue },
    )
  } else if (hasBaseline) {
    cumulativeSavingsSeries.push(
      { name: t('long_term_results.series_total_existing'), data: baselineResults.cumulativeSavings },
    )
  }

  // ---- Annual Energy Flow Chart: grouped stacked bars ----
  const fmtMWh = (v) => v != null ? `${(Math.abs(v) / 1000).toFixed(1)} MWh` : ''

  // Auto-scale Y-axis for energy flows
  const allFlowValues = [
    ...results.selfConsumedKwh, ...results.gridImportKwh,
    ...results.gridExportKwh.map(v => -v),
    ...(hasBaseline && baselineResults.selfConsumedKwh ? [
      ...baselineResults.selfConsumedKwh, ...baselineResults.gridImportKwh,
      ...baselineResults.gridExportKwh.map(v => -v),
    ] : []),
  ].filter(v => v != null)
  // For stacked bars, max is sum of positives per year; approximate with individual max + padding
  const flowMaxVal = Math.max(...allFlowValues.map(Math.abs), 0)
  const flowPadding = flowMaxVal * 0.15
  const flowMin = Math.min(...allFlowValues, 0) - flowPadding
  const flowMax = Math.max(...allFlowValues, 0) + flowPadding

  const energyFlowOptions = {
    chart: {
      type: 'bar',
      height: 350,
      stacked: true,
      toolbar: { show: false },
      animations: { enabled: true, speed: 800 },
    },
    colors: hasBaseline
      ? ['#27AE60', '#E74C3C', '#F39C12', '#93D9A3', '#F1948A', '#F9E79F']
      : ['#27AE60', '#E74C3C', '#F39C12'],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: hasBaseline ? '85%' : '60%',
      },
    },
    dataLabels: { enabled: false },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '11px',
    },
    xaxis: {
      categories: results.years,
      title: { text: t('results.time_period') },
    },
    yaxis: {
      title: { text: t('results.energy_mwh') },
      min: Math.round(flowMin / 10000) * 10000,
      max: Math.round(flowMax / 10000) * 10000,
      labels: {
        formatter: (value) => value != null ? `${(value / 1000).toFixed(0)}` : '',
      },
    },
    tooltip: {
      y: {
        formatter: fmtMWh,
      },
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
    },
  }

  // Grouped stacked bars: ApexCharts `group` property keeps stacks separate
  const energyFlowSeries = [
    { name: hasBaseline ? t('long_term_results.series_self_consumed_inv') : t('results.self_consumed'), data: results.selfConsumedKwh, group: 'investment' },
    { name: hasBaseline ? t('long_term_results.series_grid_import_inv') : t('results.grid_import'), data: results.gridImportKwh, group: 'investment' },
    { name: hasBaseline ? t('long_term_results.series_grid_export_inv') : t('results.grid_export'), data: results.gridExportKwh.map(v => -v), group: 'investment' },
    ...(hasBaseline && baselineResults.selfConsumedKwh?.length > 0 ? [
      { name: t('long_term_results.series_self_consumed_existing'), data: baselineResults.selfConsumedKwh, group: 'existing' },
      { name: t('long_term_results.series_grid_import_existing'), data: baselineResults.gridImportKwh, group: 'existing' },
      { name: t('long_term_results.series_grid_export_existing'), data: baselineResults.gridExportKwh.map(v => -v), group: 'existing' },
    ] : []),
  ]

  return (
    <div>
      {/* Page Header */}
      <div className="tool-page-header mb-4">
        <h2 className="tool-page-title">{t('long_term_results.header_title')}</h2>
        <p className="tool-page-subtitle">
          {t('long_term_results.header_subtitle', { count: numYears })}
        </p>
      </div>

      {dataSource === 'mock' && (
        <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <div>
            <strong>{t('long_term_results.sample_data_label')}</strong> {t('long_term_results.sample_data_text')}
          </div>
        </div>
      )}

      {dataSource === 'api' && propCommunity && (
        <div className="alert alert-info d-flex align-items-center mb-3 py-2" role="alert" style={{ fontSize: 'var(--font-size-sm)' }}>
          <i className="bi bi-database-check me-2"></i>
          <div>
            {t('long_term_results.real_data_prefix', { details: (() => {
              const parts = []
              const buildings = propCommunity?.buildings || []
              const assets = propCommunity?.community_assets || []
              const realPv = assets.filter(a => ['solar_pv_farm', 'ground_mounted_pv', 'pv'].includes(a.asset_type)).reduce((s, a) => s + (a.specs?.capacity_kwp || 0), 0)
                + buildings.reduce((s, b) => s + (b.building_assets || []).filter(a => a.asset_type === 'rooftop_pv').reduce((x, a) => x + (a.specs?.capacity_kwp || 0), 0), 0)
              const realBatt = assets.filter(a => ['community_battery', 'battery'].includes(a.asset_type)).reduce((s, a) => s + (a.specs?.capacity_kwh || 0), 0)
              const realWind = assets.filter(a => ['wind_turbine', 'wind'].includes(a.asset_type)).reduce((s, a) => s + (a.specs?.capacity_kwp || a.specs?.rated_power_kw || 0), 0)
              if (realPv > 0) parts.push(t('long_term_results.rd_pv', { n: realPv }))
              if (realBatt > 0) parts.push(t('long_term_results.rd_battery', { n: realBatt }))
              if (realWind > 0) parts.push(t('long_term_results.rd_wind', { n: realWind }))
              if (params?.annualLoadKwh) parts.push(t('long_term_results.rd_load', { n: Math.round(params.annualLoadKwh).toLocaleString() }))
              const whatIf = []
              if (params?.addPvSystem && params?.newPvCapacityKwp > 0) whatIf.push(t('long_term_results.rd_whatif_pv', { n: params.newPvCapacityKwp }))
              if (params?.addBattery && params?.newBatteryCapacityKwh > 0) whatIf.push(t('long_term_results.rd_whatif_battery', { n: params.newBatteryCapacityKwh }))
              if (params?.addWindTurbine && params?.newWindCapacityKw > 0) whatIf.push(t('long_term_results.rd_whatif_wind', { n: params.newWindCapacityKw }))
              if (params?.loadGrowthPct) whatIf.push(t('long_term_results.rd_load_growth', { n: params.loadGrowthPct }))
              if (params?.tariffChangePct) whatIf.push(t('long_term_results.rd_tariff_escalation', { n: params.tariffChangePct }))
              let text = parts.length > 0 ? parts.join(', ') : t('long_term_results.rd_fallback')
              if (whatIf.length > 0) text += ` | ${t('long_term_results.rd_whatif_label', { items: whatIf.join(', ') })}`
              return text
            })() })}
          </div>
        </div>
      )}

      {/* Summary KPI Cards */}
      <Row className="g-3 mb-4">
        <Col sm={6} lg={3}>
          <Card
            style={{
              border: '1px solid var(--color-neutral-200)',
              borderRadius: 'var(--radius-lg)',
              height: '100%',
            }}
          >
            <Card.Body>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', marginBottom: 'var(--space-2)' }}>
                {t('results.total_savings')}
              </div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, color: 'var(--color-success)' }}>
                {fmtK(results.summary.totalSavings)}
              </div>
              {hasBaseline && baselineResults?.summary && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginTop: 2 }}>
                  {t('long_term_results.card_vs_existing_savings', { value: fmtK(baselineResults.summary.totalSavings) })}
                  <span style={{ color: 'var(--color-success)', fontWeight: 600, marginLeft: 4 }}>
                    (+{fmtK(results.summary.totalSavings - baselineResults.summary.totalSavings)})
                  </span>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} lg={3}>
          <Card
            style={{
              border: '1px solid var(--color-neutral-200)',
              borderRadius: 'var(--radius-lg)',
              height: '100%',
            }}
          >
            <Card.Body>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', marginBottom: 'var(--space-2)' }}>
                {t('results.avg_self_sufficiency')}
              </div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, color: 'var(--color-primary)' }}>
                {results.summary.avgSelfSufficiency.toFixed(1)}%
              </div>
              {hasBaseline && baselineResults?.summary && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginTop: 2 }}>
                  {t('long_term_results.card_vs_existing_ss', { value: baselineResults.summary.avgSelfSufficiency.toFixed(1) })}
                  <span style={{ color: 'var(--color-success)', fontWeight: 600, marginLeft: 4 }}>
                    (+{(results.summary.avgSelfSufficiency - baselineResults.summary.avgSelfSufficiency).toFixed(1)}pp)
                  </span>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} lg={3}>
          <Card
            style={{
              border: '1px solid var(--color-neutral-200)',
              borderRadius: 'var(--radius-lg)',
              height: '100%',
            }}
          >
            <Card.Body>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', marginBottom: 'var(--space-2)' }}>
                {t('results.total_co2_avoided')}
              </div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, color: 'var(--color-success)' }}>
                {t('results.tons', { value: results.summary.totalCO2Avoided.toFixed(0) })}
              </div>
              {hasBaseline && baselineResults?.summary && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginTop: 2 }}>
                  {t('long_term_results.card_vs_existing_co2', { value: baselineResults.summary.totalCO2Avoided.toFixed(0) })}
                  <span style={{ color: 'var(--color-success)', fontWeight: 600, marginLeft: 4 }}>
                    (+{(results.summary.totalCO2Avoided - baselineResults.summary.totalCO2Avoided).toFixed(1)}t)
                  </span>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col sm={6} lg={3}>
          <Card
            style={{
              border: '1px solid var(--color-neutral-200)',
              borderRadius: 'var(--radius-lg)',
              height: '100%',
            }}
          >
            <Card.Body>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', marginBottom: 'var(--space-2)' }}>
                {t('results.roi')}
              </div>
              <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, color: 'var(--color-accent)' }}>
                {results.summary.roi}%
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginTop: 2 }}>
                {t('results.roi_note')}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts Row 1: KPI Projections */}
      <Row className="g-4 mb-4">
        <Col md={12}>
          <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body>
              <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                {t('results.annual_kpi_projections')}
                {hasBaseline && <span style={{ fontWeight: 400, fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginLeft: 8 }}>
                  {t('results.legend_solid_dashed')}
                </span>}
              </h6>
              <Chart
                options={kpiProjectionsOptions}
                series={kpiProjectionsSeries}
                type="line"
                height={350}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts Row 2: Energy Flow + Cumulative Savings */}
      <Row className="g-4 mb-4">
        <Col md={7}>
          <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body>
              <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                {t('results.annual_energy_flows')}
                {hasBaseline && <span style={{ fontWeight: 400, fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginLeft: 8 }}>
                  {t('results.legend_comparing')}
                </span>}
              </h6>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginBottom: 8 }}>
                {t('long_term_results.energy_flows_note')}
                {hasBaseline && t('long_term_results.energy_flows_note_baseline')}
              </div>
              <Chart
                options={energyFlowOptions}
                series={energyFlowSeries}
                type="bar"
                height={350}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col md={5}>
          <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body>
              <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                {t('results.cumulative_cost_savings')}
              </h6>
              {hasSavingsBreakdown && (
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)', marginBottom: 8 }}>
                  {t('long_term_results.savings_breakdown_note')}
                </div>
              )}
              <Chart
                options={cumulativeSavingsOptions}
                series={cumulativeSavingsSeries}
                type="area"
                height={350}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Dynamic Insights Panel */}
      <Row className="g-4 mb-4">
        <Col md={12}>
          <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body>
              <h6 style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                <i className="bi bi-lightbulb me-2" style={{ color: 'var(--color-accent)' }}></i>
                {t('long_term_results.key_insights')}
              </h6>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-700)', lineHeight: 1.7 }}>
                {generateInsights(results, baselineResults, inputSnapshot, numYears, sym, t).map((insight, i) => (
                  <div key={i} className="d-flex align-items-start mb-2">
                    <span className="me-2" style={{ flexShrink: 0 }}>{insight.icon}</span>
                    <span>{insight.text}</span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Actions */}
      {saveStatus === 'success' && (
        <Alert variant="success" className="mb-3 py-2" style={{ fontSize: 'var(--font-size-sm)' }}>
          {t('long_term_results.save_success')}
        </Alert>
      )}
      {saveStatus === 'error' && (
        <Alert variant="danger" className="mb-3 py-2" style={{ fontSize: 'var(--font-size-sm)' }}>
          {t('results.save_failed')}
        </Alert>
      )}
      <div className="d-flex gap-2 justify-content-end">
        <Button variant="outline-secondary" onClick={handleExportCSV} disabled={!results}>
          {t('results.export_csv_data')}
        </Button>
        {canEdit && (
        <Button
          variant="primary"
          style={{
            backgroundColor: 'var(--color-primary)',
            borderColor: 'var(--color-primary)',
          }}
          onClick={handleSaveResults}
          disabled={isSaving || !results}
        >
          {isSaving ? (
            <><Spinner animation="border" size="sm" className="me-1" />{t('results.saving')}</>
          ) : t('results.save_results')}
        </Button>
        )}
      </div>
    </div>
  )
}

export default LongTermResultsPage
